import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ShieldAlert, Mail, Trash2, Check, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { LEGAL_OPERATOR, LEGAL_CONTACT_EMAIL, LEGAL_LAST_UPDATED } from "@/lib/legal";

// Public URL: /delete-account
// This is the URL we paste into Google Play Console's Data Safety
// "Delete account URL" field. It must be reachable without logging in
// and must clearly describe (a) how to request deletion, (b) what is
// deleted, (c) what is retained for legal reasons and for how long.
// Do NOT gate this behind auth.

const APP_STORE_NAME = "IX NOVA"; // Name as shown on Google Play + Apple App Store listing
const APP_FULL_NAME  = "IX NOVA: AI Personal Coach";

function Section({ title, children, id }) {
  return (
    <section id={id} style={{ marginBottom: 22 }}>
      <h3 className="h3" style={{ color: "var(--text)", marginBottom: 10, fontSize: 16 }}>{title}</h3>
      <div className="body-sm" style={{ color: "var(--text-dim)", lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  );
}

export default function DeleteAccountRequestScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter the email address associated with your account.");
      return;
    }
    if (!confirmed) {
      setError("Please tick the confirmation box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/account/deletion-request", {
        email: email.trim().toLowerCase(),
        reason: reason.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const mailto = `mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent("Account deletion request — IX NOVA")}&body=${encodeURIComponent("Hello,\n\nI would like to request deletion of my IX NOVA account.\n\nAccount email: (fill in)\nReason (optional):\n\nThanks.")}`;

  return (
    <div className="screen fade-in" data-testid="delete-account-page" style={{ paddingBottom: 60 }}>
      <div className="top-bar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="delete-account-back"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">ACCOUNT</div>
        <div style={{ width: 32 }} />
      </div>

      <div className="eyebrow" style={{ marginTop: 12, marginBottom: 8 }}>
        LEGAL · LAST UPDATED {LEGAL_LAST_UPDATED.toUpperCase()}
      </div>
      <h2 className="h1" style={{ fontSize: 30, marginBottom: 12, lineHeight: 1.12 }}>
        {APP_STORE_NAME} Account Deletion
      </h2>
      <p className="body-sm" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 22, lineHeight: 1.6 }}>
        You can request permanent deletion of your {APP_FULL_NAME} account and personal data
        at any time. This page is the official {APP_STORE_NAME} account deletion channel and
        satisfies Google Play&#39;s Data Safety and Apple App Store account-deletion requirements.
      </p>

      <Section title="1. Two ways to delete your account">
        <p>
          <strong style={{ color: "var(--text)" }}>a) Inside the app (fastest — instant).</strong><br />
          If you can still sign in to {APP_STORE_NAME}, open the app and go to:
        </p>
        <ol style={{ marginTop: 8, paddingLeft: 20 }}>
          <li><strong>Profile</strong> tab (bottom navigation)</li>
          <li>Scroll down to the red <strong>&ldquo;Delete account&rdquo;</strong> option</li>
          <li>Confirm your password to authorise the deletion</li>
          <li>Your account and all associated data are permanently deleted immediately</li>
        </ol>

        <p style={{ marginTop: 14 }}>
          <strong style={{ color: "var(--text)" }}>b) Using the form below (if you can&#39;t sign in).</strong><br />
          Enter the email address you used to register. We&#39;ll verify your identity by email
          and complete the deletion within 30 days, or sooner. You will receive a confirmation
          email at every step.
        </p>
      </Section>

      <Section title="2. What data will be deleted">
        <p>When your deletion is processed, the following are permanently removed from our production systems:</p>
        <ul style={{ marginTop: 8, paddingLeft: 20 }}>
          <li>Your account credentials (email address, hashed password, sessions and tokens)</li>
          <li>Your profile information (name, avatar photo, age, gender, height, weight, goals, dietary preferences, allergies, skin sensitivities)</li>
          <li>All photos you uploaded (nutrition scans, skin/hair coaching photos, outfit ratings, progress tracking)</li>
          <li>Your health &amp; lifestyle inputs (meal history, workouts, streaks, notes, coach conversations)</li>
          <li>Community posts you created, comments you posted, reports you filed, users you blocked</li>
          <li>Any notification preferences and device push tokens</li>
          <li>Any onboarding answers and quiz results</li>
        </ul>
      </Section>

      <Section title="3. What is kept temporarily and why">
        <p>A small amount of data must be retained after account deletion. This is limited to what is legally required or strictly necessary for security and financial-record purposes:</p>
        <ul style={{ marginTop: 8, paddingLeft: 20 }}>
          <li><strong>Payment records (subscriptions, receipts):</strong> retained by our payment processor Stripe and referenced by an anonymised customer ID for up to <strong>7 years</strong>, as required by U.S. and E.U. tax and financial-reporting law. These records do NOT contain your name, email, or profile — only transaction amounts, dates, and Stripe&#39;s own customer identifier.</li>
          <li><strong>Abuse and moderation logs</strong> (e.g. reports you filed or reports filed against you, if any): retained in anonymised form for up to <strong>12 months</strong> to protect other users and to comply with applicable safety obligations.</li>
          <li><strong>Backup snapshots:</strong> your data may persist in encrypted, offline database backups for up to <strong>30 days</strong> before those backups are rotated out and destroyed. Backups are not used for active processing and are never restored except to recover from a catastrophic outage.</li>
          <li><strong>Email delivery logs</strong> (from our email provider Resend): sender/recipient/subject metadata for up to <strong>7 days</strong> for spam and deliverability protection. Email bodies are not retained.</li>
        </ul>
        <p style={{ marginTop: 12 }}>
          Nothing in this section is used to re-identify you, market to you, or restore your account.
        </p>
      </Section>

      <Section title="4. Timeline">
        <p>
          In-app deletions complete <strong>immediately</strong>. Web form requests are processed
          within <strong>30 days</strong> (usually within 48 hours). You&#39;ll receive an
          acknowledgement email at the address you provide, and a final confirmation email once
          deletion is complete.
        </p>
      </Section>

      {/* ---------------- The request form ---------------- */}
      <Section title="5. Request deletion by email">
        {!submitted ? (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
            <div className="field">
              <label className="label" htmlFor="del-email">Account email</label>
              <input
                id="del-email"
                type="email"
                required
                className="input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                data-testid="delete-account-email-input"
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="del-reason">Reason (optional)</label>
              <textarea
                id="del-reason"
                className="input"
                placeholder="Anything you&#39;d like us to know. Optional."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                data-testid="delete-account-reason-input"
                style={{ resize: "vertical", fontFamily: "inherit" }}
              />
            </div>

            <label
              style={{
                display: "flex", alignItems: "flex-start", gap: 10, marginTop: 4,
                fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5, cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="delete-account-confirm-checkbox"
                style={{ marginTop: 3, flexShrink: 0 }}
              />
              <span>
                I understand that this request will permanently delete my {APP_STORE_NAME} account
                and personal data. This cannot be undone. I am the owner of the email address
                entered above.
              </span>
            </label>

            {error && (
              <div
                data-testid="delete-account-error"
                style={{
                  display: "flex", alignItems: "center", gap: 8, marginTop: 4,
                  color: "#ffb0b0", fontSize: 13,
                }}
              >
                <AlertCircle size={14} strokeWidth={1.9} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              data-testid="delete-account-submit-btn"
              style={{
                marginTop: 6, width: "100%", justifyContent: "center",
                display: "flex", alignItems: "center", gap: 8,
                background: "#3a1414", borderColor: "#5a1e1e", color: "#ffd0d0",
              }}
            >
              <Trash2 size={15} strokeWidth={1.9} />
              {submitting ? "Sending request…" : "Submit deletion request"}
            </button>
          </form>
        ) : (
          <div
            data-testid="delete-account-submitted"
            style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 14, padding: "22px 8px", textAlign: "center",
              background: "var(--bg-elev-1, #141414)", border: "1px solid var(--line, #1f1f1f)",
              borderRadius: 12,
            }}
          >
            <div
              style={{
                width: 56, height: 56, borderRadius: "50%",
                background: "rgba(232,211,185,0.15)", color: "var(--gold, #e8d3b9)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Check size={26} strokeWidth={1.8} />
            </div>
            <div style={{ fontSize: 17, color: "var(--text)", fontWeight: 500 }}>
              Deletion request received.
            </div>
            <div style={{ fontSize: 13, color: "var(--text-dim)", maxWidth: 360, lineHeight: 1.6 }}>
              We&#39;ve sent a confirmation to <strong style={{ color: "var(--text)" }}>{email}</strong>.
              Your account will be permanently deleted within 30 days (usually within 48 hours).
              You&#39;ll receive a final email once the deletion is complete.
            </div>
          </div>
        )}
      </Section>

      <Section title="6. Prefer to email us directly?">
        <p style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Mail size={14} strokeWidth={1.8} />
          <a href={mailto} style={{ color: "var(--gold, #e8d3b9)" }} data-testid="delete-account-mailto">
            {LEGAL_CONTACT_EMAIL}
          </a>
        </p>
        <p style={{ marginTop: 8 }}>
          Send your account email address and the subject line <em>&ldquo;Account deletion request&rdquo;</em>.
          We&#39;ll reply within 48 hours to confirm receipt.
        </p>
      </Section>

      <Section title="7. Publisher &amp; contact">
        <p style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <ShieldAlert size={14} strokeWidth={1.8} style={{ marginTop: 4, flexShrink: 0 }} />
          <span>
            <strong style={{ color: "var(--text)" }}>{APP_FULL_NAME}</strong><br />
            Published by <strong>{LEGAL_OPERATOR}</strong>.<br />
            Privacy contact: <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} style={{ color: "var(--gold, #e8d3b9)" }}>{LEGAL_CONTACT_EMAIL}</a>
          </span>
        </p>
      </Section>
    </div>
  );
}
